import React, {useState, useEffect} from 'react';

import * as S from './DropDownCheckBox.styles';

import ArrowDownIcon from '../../../../Icons/ArrowDownIcon';
import CheckBoxUncheckedIcon from '../../../../Icons/CheckBoxUncheckedIcon';
import CheckBoxCheckedIcon from '../../../../Icons/CheckBoxCheckedIcon';
import useArrayToggler, {
  ArrayTogglerBehaviorTypes,
} from '../../../../../shared/hooks/useArrayToggler';

import {defaultTheme} from '@stencil/build-theme';

import HappyArrowDropIcon from '@stencil/icons/src/HappyArrowDropIcon/HappyArrowDropIcon';

enum DispatchActionTypes {
  updateTriggerTextHoverState = 'updateTriggerTextHoverState',
  updatePopperHoverState = 'updatePopperHoverState',
}

enum HoverState {
  mouseOver = 'mouseOver',
  mouseOff = 'mouseOff',
}

const initState = {
  triggerTextHoverState: HoverState.mouseOff,
  popperHoverState: HoverState.mouseOff,
  changedBecauseOf: DispatchActionTypes,
};

type ReducerActionType =
  | {
      type: DispatchActionTypes.updateTriggerTextHoverState;
      payload: {
        hoverState: HoverState;
      };
    }
  | {
      type: DispatchActionTypes.updatePopperHoverState;
      payload: {
        hoverState: HoverState;
      };
    };

function reducer(state: typeof initState, action: ReducerActionType) {
  switch (action.type) {
    case DispatchActionTypes.updateTriggerTextHoverState:
      return {
        ...state,
        triggerTextHoverState: action.payload?.hoverState,
        changedBecauseOf: DispatchActionTypes.updateTriggerTextHoverState,
      };
    case DispatchActionTypes.updatePopperHoverState:
      return {
        ...state,
        popperHoverState: action.payload?.hoverState,
        changedBecauseOf: DispatchActionTypes.updatePopperHoverState,
      };
    default:
      return state; // should ideally throw an error
  }
}

function DropDownCheckBox<T extends unknown, K extends keyof T>({
  title,
  data,
  lookup,
  actionHandler,
  resetHandlerProp,
  renderItem,
  defaultState,
  type = ArrayTogglerBehaviorTypes.multiSelect,
  width = '',
}: {
  title: string;
  data: T[];
  lookup: (item: T) => T[K] | T;
  actionHandler: (data: T[]) => void;
  resetHandlerProp?: () => void;
  renderItem: (item: T) => JSX.Element;
  defaultState?: T[];
  type?: ArrayTogglerBehaviorTypes;
  width?: string;
}) {
  const [isChecked, onClickHandler, resetHandler] = useArrayToggler(
    data,
    lookup,
    type,
    actionHandler,
    defaultState,
  );

  const [state, dispatch] = React.useReducer(reducer, initState);

  const popperOpenState =
    state.triggerTextHoverState === HoverState.mouseOver ||
    state.popperHoverState === HoverState.mouseOver;

  return (
    <>
      <S.HoverTargetWrapperDiv>
        <S.WrapperDiv
          onMouseEnter={() => {
            dispatch({
              type: DispatchActionTypes.updateTriggerTextHoverState,
              payload: {
                hoverState: HoverState.mouseOver,
              },
            });
          }}
          onMouseLeave={() => {
            dispatch({
              type: DispatchActionTypes.updateTriggerTextHoverState,
              payload: {
                hoverState: HoverState.mouseOff,
              },
            });
          }}
        >
          <span>{title}</span>
          <S.WrapperSpan>
            <HappyArrowDropIcon
              width="1rem"
              height="1rem"
              margin="0 0 0 0.5rem"
              fill={defaultTheme.colors.goBrandBlue.base}
            />
          </S.WrapperSpan>
        </S.WrapperDiv>
        {popperOpenState && (
          <S.HiddenDropdownWrapperDiv
            width={width}
            onMouseEnter={e => {
              e.stopPropagation();
              dispatch({
                type: DispatchActionTypes.updatePopperHoverState,
                payload: {
                  hoverState: HoverState.mouseOver,
                },
              });
            }}
            onMouseLeave={e => {
              e.stopPropagation();
              dispatch({
                type: DispatchActionTypes.updatePopperHoverState,
                payload: {
                  hoverState: HoverState.mouseOff,
                },
              });
            }}
          >
            <S.TopDiv>
              <S.QuickFiltersSticky>
                <S.FontBoldUppercasedSpan>{title}</S.FontBoldUppercasedSpan>
                <S.ClearAllButton
                  onClick={() => {
                    resetHandler();
                    if (resetHandlerProp) resetHandlerProp();
                  }}
                >
                  Clear All
                </S.ClearAllButton>
              </S.QuickFiltersSticky>
              <S.SortingDropDownWrapper>
                {data.map((item, i) => (
                  <li onClick={() => onClickHandler(item)} key={i}>
                    {(isChecked(item) && (
                      <CheckBoxCheckedIcon height="1.6rem" width="1.6rem" />
                    )) || <CheckBoxUncheckedIcon height="1.6rem" width="1.6rem" />}
                    {renderItem(item)}
                  </li>
                ))}
              </S.SortingDropDownWrapper>
              <S.TipPositioning />
            </S.TopDiv>
          </S.HiddenDropdownWrapperDiv>
        )}
      </S.HoverTargetWrapperDiv>
    </>
  );
}

export default DropDownCheckBox;
